import React, { useEffect, useState } from 'react';
import { api } from './api';
import { RangePlayer } from './BeforeAfter';
import { takeStamp } from '../../shared/takeStamp';
import { TakeRef, stampText } from './takeRef';

// The open turn, the part that is always there: the questions a person owes
// on it, every remark with one sentence on where it stands, and the turn as
// the active take sounds (the stretch with the remarks, their places marked).

const ACUTE = '́';
const VOWELS = /[аеёиоуыэюяaeiouy]/i;

// «форпост» -> ['фо́рпост', 'форпо́ст']: the word with the mark after each vowel
// (up to 5; a longer word is answered in the field).
function stressVariants(word) {
  const w = String(word || '').replace(new RegExp(ACUTE, 'g'), '');
  const out = [];
  for (let i = 0; i < w.length; i += 1) if (VOWELS.test(w[i])) out.push(`${w.slice(0, i + 1)}${ACUTE}${w.slice(i + 1)}`);
  return out.length <= 5 ? out : [];
}

// Remarks that name a whole stretch (a paragraph, a turn — pauses, level,
// delivery): in the text they would light a whole paragraph and hide the
// words that matter, so only remarks that name words are lit.
export const soundRemarkIds = (turn) => new Set((turn && turn.cards ? turn.cards : []).filter((c) => c.names === 'stretch').map((c) => c.taskId));
export const withoutSound = (played, ids) => (played && ids && ids.size ? { ...played, marks: (played.marks || []).filter((m) => !ids.has(m.taskId)) } : played);

export const owesAnswer = (c) => c.phase === 'undecided' && !c.accepted;
// a doubt about a word's stress: one button per vowel; a word with no vowels to pick from (a number) is asked like any other question
const stressQuestion = (c) => owesAnswer(c) && !c.pendingTextRunId && c.word && !c.stressed && stressVariants(c.word).length > 0;

// The remark's editorial state in this turn. Agent execution and controls
// belong to the turn dialog, not to individual remark documents.
export function remarkState(c, turn) {
  const owed = (turn.cards || []).some((x) => owesAnswer(x) && x.taskId !== c.taskId);
  const myEdit = (turn.edits || []).find((e) => e.taskId === c.taskId);
  if (c.accepted && c.acceptedMark && c.acceptedMark.manual) return { text: `${c.acceptedMark.by} marked this remark as resolved here${c.acceptedMark.versionId ? ` in take ${stampText(c.acceptedMark.versionId)}` : ''}. No new recording was made for this mark.` };
  if (c.accepted) return { text: 'This remark is marked as resolved in this turn.' };
  if (c.phase === 'done') return { text: 'This remark is closed.' };
  if (owesAnswer(c)) return { text: c.pendingTextRunId ? 'A text proposal awaits review. Review or reject it above before recording.' : 'Review the question above before recording. You can answer it or dismiss the remark.' };
  if (turn.fragment && turn.fragment.acceptWhenMeasured) return { text: 'You already requested applying this take. The system is measuring word boundaries; no second decision is needed.' };
  if (turn.fragment && turn.fragment.acceptError) return { text: `Applying the chosen take failed: ${turn.fragment.acceptError.error}. Retry applying it below; you do not need to judge the take again.` };
  if (myEdit && !myEdit.accepted) return { text: 'An audio edit is ready for this card. Compare Before and After in the edit block; Accept applies it, Reject declines it.' };
  if (turn.draft) return { text: 'A Draft of this turn is open below. Compare it with the current recording and check this remark before Accept.' };
  if (turn.fragment && ['waiting', 'candidate', 'clear'].includes(c.phase)) return { text: `${turn.fragment.reused ? 'An earlier take is offered for this turn.' : 'A new take is ready for this turn.'} Compare Before and After below and check this remark. Accept applies the recording; Edit lets you combine parts from several takes.` };
  if (owed) return { text: 'Other remarks on this turn still have questions. Answer them before recording so one take can include all agreed changes.' };
  if (['waiting', 'clear'].includes(c.phase)) return { text: 'Awaiting a new recording of this turn.' };
  if (c.phase === 'remark' && (turn.requested || turn.fragment)) return { text: 'This remark concerns the sound. Check it when comparing takes: a new recording alone does not mean it is fixed.' };
  return { text: '' };
}

// The questions a person owes on the turn. A stress: one button per vowel;
// anything else: an answer in the turn's dialog.
export function TurnQuestions({ turn, field = null, onOpenCard, onChanged, onAnswer }) {
  const owed = [];
  const seen = new Set();
  for (const c of turn.cards || []) if (owesAnswer(c) && !c.pendingTextRunId && !seen.has(c.taskId)) { seen.add(c.taskId); owed.push(c); }
  if (!owed.length) return null;
  return <>{owed.map((c) => <Question key={c.taskId} c={c} turnIndex={turn.i} field={field} onOpenCard={onOpenCard} onChanged={onChanged} onAnswer={onAnswer} />)}</>;
}

function Question({ c, turnIndex = null, field = null, onOpenCard, onChanged, onAnswer }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  // Where the answered stress goes: the glossary (every place of this form in
  // the quest, the derived forms too) or this turn's place(s) of the word only
  // — the studio's «Only here».
  const [scope, setScope] = useState('glossary');
  const act = async (fn) => { setBusy(true); setError(null); try { await fn(); if (onChanged) onChanged(); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const variants = stressQuestion(c) ? stressVariants(c.word) : [];
  const answer = (v) => act(() => api.answerStress(c.taskId, c.targetId, v, scope === 'here' ? { scope: 'here', turn: turnIndex, field } : {}));
  return (
    <div className="tk-question">
      {stressQuestion(c) ? (
        <>
          <div className="tk-question-ask"><strong>Choose the stress:</strong> how is «{c.word}» pronounced{variants.length ? ` — ${variants.join(' or ')}` : ''}?</div>
          <div className="tk-muted">Your choice will be included in the text for the next recording. You can clarify the other remarks on this turn before starting it.</div>
          <div className="tk-span-toggle" role="group" aria-label="Where the stress goes">
            <button type="button" className={`tk-tab ${scope === 'glossary' ? 'on' : ''}`} onClick={() => setScope('glossary')} title="A rule of the quest: every place of this form (and its inflected forms) without a mark of its own is read so">To the glossary</button>
            <button type="button" className={`tk-tab ${scope === 'here' ? 'on' : ''}`} onClick={() => setScope('here')} disabled={turnIndex == null || !field} title="The mark goes into this turn's place(s) of the word only; the glossary and every other place stay as they are — the studio's «Only here»">Only here</button>
          </div>
          <div className="tk-row">
            {variants.map((v) => <button key={v} type="button" className="tk-btn tk-btn-small" disabled={busy} onClick={() => answer(v)} title={scope === 'here' ? `«${v}» at this turn's place only` : `«${v}» into the glossary, quest-wide`}>{v}</button>)}
          </div>
        </>
      ) : (
        <>
          <div className="tk-question-ask"><strong>Clarification needed:</strong> {c.uncertainty || c.summary || c.title}</div>
          <div className="tk-muted">Your answer helps prepare the text for the next recording along with the other changes to this turn.</div>
          <div className="tk-row">
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Answer the turn agent here" aria-label="Answer" />
            <button type="button" className="tk-btn tk-btn-small" disabled={busy || !text.trim() || !onAnswer} onClick={() => act(async () => { await onAnswer(c, text.trim()); setText(''); })}>Send</button>
          </div>
        </>
      )}
      <button type="button" className="tk-btn tk-btn-ghost tk-btn-small" disabled={busy} onClick={() => act(() => api.update(c.taskId, { status: 'dismissed', note: 'Dismissed from the turn question.' }))} title="Dismiss this remark card at all its locations. It can be reopened from the card list.">Dismiss remark</button>
      <div className="tk-muted">Card: <button type="button" className="tk-linkbtn" onClick={() => onOpenCard && onOpenCard(c.taskId)}>{c.title}</button></div>
      {error ? <div className="tk-error">{error}</div> : null}
    </div>
  );
}

// The turn as the active take sounds: the stretch with the remarks (or the
// whole turn), their places marked on the bar. A take whose timings are not
// measured yet plays whole at once (the server puts it in the aligner's line);
// the stretch and the marks arrive when the timings land — the player keeps
// playing meanwhile (RangePlayer takes the new range up after the play).
export function ActiveTurnAudio({ questId, locationUuid, field, turnIndex, activeVersionId, active = null, hover, soundIds = null }) {
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [whole, setWhole] = useState(false);
  // The aligner's ticket while the take's timings are measured; `again`
  // re-reads the take (without dropping what is shown) once they land.
  const [run, setRun] = useState(null);
  const [again, setAgain] = useState(0);
  // another turn or another active take: start from nothing
  useEffect(() => { setP(null); setError(null); setRun(null); }, [questId, locationUuid, field, turnIndex, activeVersionId]);
  useEffect(() => {
    let live = true;
    api.turnAudio(questId, { locationUuid, field, turn: turnIndex }).then((r) => {
      if (!live) return;
      setError(null);
      setRun(r.measuring ? { ...r.measuring, versionId: r.activeVersionId } : null);
      setP(r);
    }).catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [questId, locationUuid, field, turnIndex, activeVersionId, again]);
  const runTaskId = run ? run.taskId : null;
  const runStatus = run ? run.status : null;
  const runVersionId = run ? run.versionId : null;
  // The server announces every change of an alignment (SSE → qv:alignment):
  // done — read the take again; failed — show why, with the runner's tail.
  useEffect(() => {
    if (!runVersionId) return undefined;
    const on = (e) => {
      const d = e.detail || {};
      if (Number(d.versionId) !== Number(runVersionId)) return;
      if (d.status === 'done') setAgain((n) => n + 1);
      else if (d.status === 'failed') setRun((cur) => (cur ? { ...cur, status: 'failed', error: d.error || cur.error, output: d.output || cur.output } : cur));
      else if (d.status) setRun((cur) => (cur ? { ...cur, status: d.status } : cur));
    };
    window.addEventListener('qv:alignment', on);
    return () => window.removeEventListener('qv:alignment', on);
  }, [runVersionId]);
  // The ticket is polled too (the place in the line, the minutes): a missed
  // event is then caught within seconds.
  useEffect(() => {
    if (!runTaskId || runStatus === 'failed') return undefined;
    let live = true;
    const id = setInterval(async () => {
      try {
        const t = await api.alignRun(runTaskId);
        if (!live) return;
        if (t.status === 'done') { setAgain((n) => n + 1); return; }
        setRun((cur) => (cur ? { ...cur, status: t.status, queued: t.queued, elapsed: t.elapsed, error: t.error, output: t.output } : cur));
      } catch (e) {
        if (!live) return;
        // The line lives in the server's memory: after a restart the ticket is
        // gone (404), and reading the take again queues it afresh.
        if (e.status === 404) setAgain((n) => n + 1);
        else setRun((cur) => (cur ? { ...cur, status: 'failed', error: e.message } : cur));
      }
    }, 5000);
    return () => { live = false; clearInterval(id); };
  }, [runTaskId, runStatus]);
  if (error && !p) return <div className="tk-muted">Could not load the current recording: {error}</div>;
  if (!p) return <div className="tk-muted">Loading the current recording…</div>;
  if (p.none) return <div className="tk-muted">No active recording has been selected for this text.</div>;
  const range = !whole && p.span ? { ...p.take, ...p.span } : p.take;
  let measuringNote = null;
  if (run && run.status === 'failed') {
    measuringNote = (
      <div className="tk-muted">
        Could not measure word boundaries in take <TakeRef id={run.versionId} />: {run.error || 'no reason given'}.{' '}
        <button type="button" className="tk-linkbtn" onClick={() => setAgain((n) => n + 1)}>Try again</button>
        {run.output ? <details className="tk-fold"><summary>Aligner output</summary><pre className="tk-pre">{String(run.output).slice(-4000)}</pre></details> : null}
      </div>
    );
  } else if (run) {
    const where = run.status === 'running'
      ? `measuring word boundaries${run.elapsed ? `, about ${Math.max(1, Math.round(run.elapsed / 60))} min elapsed` : ''}`
      : `queue position: ${run.queued || 1}`;
    const how = run.already
      ? `Measurement was requested earlier`
      : `Opening this turn requested word-boundary measurement`;
    measuringNote = <div className="tk-muted">{how}: {where}. You can listen to the full recording now. Once measured, the passage with the remarks and precise timeline marks will appear here.</div>;
  }
  return (
    <div className="tk-active-turn">
      <div className="tk-block-label">The active take · {takeStamp(active && active.versionId === p.activeVersionId ? active : { versionId: p.activeVersionId })}</div>
      {p.span ? (
        <div className="tk-span-toggle" role="group" aria-label="What the player covers">
          <button type="button" className={`tk-tab ${!whole ? 'on' : ''}`} onClick={() => setWhole(false)}>Remarks only</button>
          <button type="button" className={`tk-tab ${whole ? 'on' : ''}`} onClick={() => setWhole(true)}>Whole turn</button>
        </div>
      ) : null}
      <RangePlayer studio={{ locationUuid, field }} versionId={range.versionId} start={range.start} end={range.end} label={run ? 'Active take · whole, timings not measured yet' : 'Active take · active now'} marks={(p.marks || []).map((m, i) => ({ key: `${m.taskId}-${i}`, t: m.t, title: m.title, lit: hover === m.taskId }))} />
      {measuringNote}
      {error ? <div className="tk-muted">Could not refresh the recording data: {error}</div> : null}
      {(!whole && p.playedSpan) || p.played ? <PlayedPlain played={withoutSound(!whole && p.playedSpan ? p.playedSpan : p.played, soundIds)} hover={hover} /> : null}
    </div>
  );
}

// Where the text under a player comes from (played.textSource): the text the
// take was sent to TTS with; else the point's text; else the aligner's own.
export function textSourceNote(played) {
  if (!played || !played.textSource) return null;
  const v = played.textVersionId ? `take ${stampText(played.textVersionId)}` : 'this take';
  if (played.textSource === 'tts') return `the text sent to TTS for ${v}`;
  if (played.textSource === 'turn') return 'the current turn text; word boundaries are not measured yet, so the full turn is shown';
  if (played.textSource === 'point') return `the current point text; the TTS request text for ${v} was not saved`;
  return `the text used to measure word boundaries for ${v}; its TTS request text was not saved, and the current point text differs`;
}

function PlayedPlain({ played, hover }) {
  const body = played.text || '';
  const marks = [...(played.marks || [])].sort((a, b) => a.start - b.start);
  const out = [];
  let at = 0;
  marks.forEach((m, i) => {
    let e = m.end;
    while (e < body.length && body[e] === ACUTE) e += 1;
    if (m.start < at) return;
    if (m.start > at) out.push(<span key={`t${i}`}>{body.slice(at, m.start)}</span>);
    out.push(<mark key={`m${i}`} className={hover === m.taskId ? 'tk-mark-lit' : ''} title={m.title}>{body.slice(m.start, e)}</mark>);
    at = e;
  });
  if (at < body.length) out.push(<span key="tail">{body.slice(at)}</span>);
  const note = textSourceNote(played);
  return (
    <>
      {note ? <div className="tk-muted">Showing {note}. Remark locations are highlighted.</div> : null}
      <div className="tk-voiced-text tk-fragment-text">{played.clippedLead ? '… ' : ''}{out}{played.clippedTail ? ' …' : ''}</div>
    </>
  );
}
